// import node module libraries
import { Fragment, useState, useEffect } from 'react';
import { Col, Form, Row, Card, Tab, Button, Offcanvas, Image, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// import custom components
import GridListViewButton from 'components/elements/miscellaneous/GridListViewButton';

// import sub components
import UsersGridCardSettings from './UsersGridCardSettings';
import UsersListItemsSettings from './UsersListItemsSettings';

// import media files
import Avatar from 'assets/images/avatar/avatar.jpg';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const UsersSettings = ({ activeKey }) => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoadingButtonUserCreate, setIsLoadingButtonUserCreate] = useState(null); // State for loading spinner
	const [showCreateUserOffcanvas, setShowCreateUserOffcanvas] = useState(false);

	const handleShowCreateUser = () => setShowCreateUserOffcanvas(true);
	const handleCloseCreateUser = () => setShowCreateUserOffcanvas(false);

	const handleChangeCreateUserRole = (e) => {
		const role_id = e.target.checked ? "a237047f-cdd6-11ef-8d7a-0242ac120009" : "f65b80f5-cf5d-11ef-8d7a-0242ac120009";
		document.getElementById('role_id').value = role_id;
	};

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const handleCreateUser = async (e) => {
		e.preventDefault();

		//setIsButtonDisabled(true);
		setIsLoadingButtonUserCreate(true); // Set loading to true

		const data = {
			user_first_name: e.target.firstname.value,
			user_last_name: e.target.lastname.value,
			user_username: e.target.username.value,
			user_password: e.target.password.value,
			role_id: e.target.role_id.value,
			user_avatar: null, // not implemented yet
			user_password_reset_token: null // not implemented yet
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/users`, data, options)
			.then(response => {
				handleShowNotification(setNotification, 'success', 'Erfolgreich', response.data.message);
				setIsLoadingButtonUserCreate(false); // Stop loading
				handleCloseCreateUser();

			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine eingegebenen Daten oder kontaktiere den HelpDesk.');
				setIsLoadingButtonUserCreate(false); // Stop loading
			});
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Tab.Container defaultActiveKey="grid">
						<Row>
							<Col lg={12} md={12} sm={12}>
								<div className="border-bottom pb-4 mb-4 d-flex align-items-center justify-content-end">
									<Button variant="secondary" className="me-2" onClick={handleShowCreateUser}>
										{t('Create User')}
									</Button>
									<GridListViewButton keyGrid="grid" keyList="list" />
								</div>
							</Col>
						</Row>

						<Tab.Content>
							<Tab.Pane eventKey="grid" className="pb-4">
								<UsersGridCardSettings activeKey={activeKey}/>
							</Tab.Pane>
							<Tab.Pane eventKey="list" className="pb-4">
								<Card className="mb-5 ">
									<Card.Body className="p-0">
										<UsersListItemsSettings activeKey={activeKey}/>
									</Card.Body>
								</Card>
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</Col>
			</Row>
			{/* Create User Offcanvas */}
			<Offcanvas show={showCreateUserOffcanvas} onHide={handleCloseCreateUser} placement="end">
				<Offcanvas.Header closeButton>
					<Offcanvas.Title><h2>{t('Create User')}</h2></Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body>
					{/* Form */}
					<Form onSubmit={handleCreateUser}>
						<Card className="mb-4">
							<Card.Header className="card-header-height d-flex justify-content-between align-items-center">
								<h4 className="mb-0">{t('Personal Information')}</h4>
							</Card.Header>
							<Card.Body className="">
								<div className="text-center">
									<Image
										src={Avatar}
										className="rounded-circle avatar-xl mb-3"
										alt=""
									/>
								</div>
								<Row className="mb-4">
									<Col lg={12} md={12} className="mb-0">
										{/* First Name */}
										<Form.Group className="mb-3">
											<Form.Label htmlFor="password">{t('First Name')}</Form.Label>
											<Form.Control
												type="text"
												id="firstname"
												placeholder={t('Enter First Name')}
												autoComplete="given-name"
												required
											/>
										</Form.Group>
									</Col>
									<Col lg={12} md={12} className="mb-0">
										{/* Last Name */}
										<Form.Group className="mb-3">
											<Form.Label htmlFor="password">{t('Last Name')}</Form.Label>
											<Form.Control
												type="text"
												id="lastname"
												placeholder={t('Enter Last Name')}
												autoComplete="family-name"
												required
											/>
										</Form.Group>
									</Col>
									<Col lg={12} md={12} className="mb-0">
										{/* E-Mail */}
										<Form.Group className="mb-3">
											<Form.Label htmlFor="password">{t('Email')}</Form.Label>
											<Form.Control
												type="email"
												id="username"
												placeholder={t('Enter Email')}
												autoComplete="username"
												required
											/>
										</Form.Group>
									</Col>
									<Col lg={12} md={12} className="mb-0">
										{/* New password */}
										<Form.Group className="mb-3">
											<Form.Label htmlFor="password">{t('Initial Password')}</Form.Label>
											<Form.Control
												type="text"
												id="password"
												placeholder={t('Enter Initial Password')}
												autoComplete="new-password"
												required
											/>
										</Form.Group>
										<div className="d-flex justify-content-end mt-4">
											<Button variant="outline-secondary" onClick={() => {
												const generatePassword = () => {
													const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
													let password = '';
													for (let i = 0; i < 12; i++) {
														password += chars.charAt(Math.floor(Math.random() * chars.length));
													}
													return password;
												};
												document.getElementById('password').value = generatePassword();
											}}>
												{t('Generate Password')}
											</Button>
										</div>
									</Col>
								</Row>
							</Card.Body>
						</Card>
						<Card className="mb-4">
							<Card.Header className="card-header-height d-flex justify-content-between align-items-center">
								<h4 className="mb-0">{t('Permissions')}</h4>
							</Card.Header>
							<Card.Body className="">
								<Row className="mb-0">
									<Col lg={12} md={12} className="mb-0">
										{/* Role */}
										<div className="d-flex justify-content-between px-3 mt-2">
											<span>{t('Company Administrator')}</span>
											<Form.Check
												type="checkbox"
												label=""
												className="form-switch"
												onChange={handleChangeCreateUserRole}
											/>
											<input type="hidden" id="role_id" value="f65b80f5-cf5d-11ef-8d7a-0242ac120009" />
										</div>
									</Col>
								</Row>
							</Card.Body>
						</Card>
						<div className="d-flex justify-content-end mt-4">
							<Button variant="secondary" onClick={handleCloseCreateUser}>
								{t('Cancel')}
							</Button>
							<Button className="ms-2 d-end" variant="primary" type="submit" disabled={isLoadingButtonUserCreate}>
								{isLoadingButtonUserCreate ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" />
										{t('Create User')}
									</>
								) : (
									t('Create User')
								)}
							</Button>
						</div>
						
					</Form>
				</Offcanvas.Body>
			</Offcanvas>
		</Fragment>
	);
};
export default UsersSettings;
